import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { lastValueFrom, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

import { DataService } from './data.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _isAuthenticated = false;
  private _isWinnings = false;
  private _token = '';
  private _tokenTimer: any;
  private _tokenExpiresIn = 0;
  private _tokenExpiresAt = 0;
  private _userId = '';
  private _supplier: any;
  private _email = '';
  private _userName = '';
  private _userAvatarUrl = '';
  private _lastActivity = Math.round(Date.now() / 1000); // seconds since epoch
  private _authStatusListener = new BehaviorSubject<string>('');
  private _winningsDomains = ['winning.com.au'];
  private _googleClientId = '494035985982-tfn53f88kh6i8kk3l2pa83cof2021l9j.apps.googleusercontent.com';
  pageInfo = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private dataService: DataService,
  ) { }

  get token() {
    return this._token;
  }

  get isWinnings() {
    return this._isWinnings;
  }

  get supplier() {
    return this._supplier;
  }

  get isAuthenticated() {
    return this._isAuthenticated;
  }

  get userId() {
    return this._userId;
  }

  get userAvatarUrl() {
    return this._userAvatarUrl;
  }

  get email() {
    return this._email;
  }

  set userId(userId: string) {
    this._userId = userId;
  }

  get authStatusListener() {
    return this._authStatusListener;
  }

  get googleClientId() {
    return this._googleClientId;
  }

  register(email: string) {
    this._email = email;
    return this.http.post<IRegister>(environment.baseUrl + '/user/register', { email });
  }

  trace(activity: string) {
    this.http.post<{ status: string, error?: string; }>(environment.baseUrl + '/user/trace', { userId: this._userId, activity }).subscribe();
  }

  changePassword(email: string, passPhrase: string, authCode: number) {
    return this.http.post<{ status: string, error?: string; }>(environment.baseUrl + '/user/changepassphrase', {
      email,
      passPhrase,
      authCode
    });
  }

  registerConfirm(email: string, name: string, passPhrase: string, authCode: number) {
    return this.http.post<{ status: string, error?: string; }>(environment.baseUrl + '/user/registerconfirm', {
      email,
      name,
      passPhrase,
      authCode
    });
  }

  updateActivity(event: any = null) {
    if (this._isAuthenticated) {
      this._lastActivity = Math.round(Date.now() / 1000);
      // we can do something with the event if required
    }
  }

  googleRegister(credential: string) {
    const googleUser = JSON.parse(atob(credential.split('.')[1]));
    // this._email = googleUser.email;
    // this._userAvatarUrl = googleUser.picture;
    // this.saveAuthData({ avatar: this._userAvatarUrl });
    return this.http.post<IRegister>(environment.baseUrl + '/user/registerwithgoogle', {
      credential
    });
  }

  // works as the lostpassword form email address validator and will send email with authcode if valid email
  lostPasswordEmail(email: string) {
    return this.http.post<{ status: string, error?: string; }>(environment.baseUrl + '/user/emailauthcode', { email });
  }

  login(email: string, passPhrase: string | undefined, googleUid?: string) {
    this._email = email;
    this.http
      .post<{ status: string; token: string; userId: string; userName: string; supplier: any; expiresIn: number; role: string; avatar: string; error?: any; }>(environment.baseUrl + '/user/login',
        {
          email,
          passPhrase,
          googleUid
        })
      .subscribe({
        next: async (response) => {
          this._userId = response.userId;
          this._userName = response.userName;
          this._supplier = this.dataService.supplier = response.supplier;
          this._userAvatarUrl = response.avatar;
          this._isWinnings = this._winningsDomains.includes(this._email.split('@')[1]) || response.role === 'ADMIN';
          this.refreshLoginData(email, response.userId, response.userName, response.token, +response.expiresIn, response.supplier, response.avatar);
          this.updateActivity();
          this._isAuthenticated = true;
          this._authStatusListener.next('success');
        },
        error: (error) => {
          console.log(error.message);
          this._authStatusListener.next(error.message);
        }
      });
  }

  private setAutoLogoutTimer() {
    clearTimeout(this._tokenTimer);
    this._tokenTimer = setTimeout(() => {
      // check for recent activity and if there has been some in the last _tokenExpiresIn + 10 seconds, then re-login seamlessly by refreshing token
      if (this._lastActivity + this._tokenExpiresIn < this._tokenExpiresAt) {
        this.logout();
      } else {
        this.refreshToken();
      }
    }, this._tokenExpiresIn * 1000);
  }

  private refreshLoginData(email: string, userId: string, userName: string, token: string, expiresIn: number, supplier: any, avatar: string) {
    this._token = token;
    this._tokenExpiresIn = expiresIn - 1; // 1000ms before the token expires on the server
    this._tokenExpiresAt = Math.round(Date.now() / 1000) + expiresIn;
    this.setAutoLogoutTimer();
    const autoLogout = new Date(Date.now() + expiresIn * 1000);
    // this.saveAuthData({ email, userId, userName, token, autoLogout, supplier: btoa(supplier) });
    this.saveAuthData({ email, userId, userName, token, autoLogout, supplier: JSON.stringify(supplier), avatar });
  }

  async autoLogin() {
    const authData = this.getAuthData();
    if (Date.now() < new Date(authData['autoLogout']).getTime()) {
      this._token = authData['token'];
      this._email = authData['email'];
      this._userId = authData['userId'];
      this._userName = authData['userName'];
      this._userAvatarUrl = authData['avatar'];
      this._supplier = this.dataService.supplier = authData['supplier'] ? JSON.parse(authData['supplier']) : undefined;
      this._isWinnings = this._winningsDomains.includes(this._email.split('@')[1]);
      this._tokenExpiresIn = new Date(authData['autoLogout']).getTime() / 1000;
      this.refreshToken();
      this._isAuthenticated = true;
      this._authStatusListener.next('success');
    }
  }

  private refreshToken() {
    this.http.get<{ status: string, token: string; expiresIn: number; error?: string; }>(environment.baseUrl + '/user/refreshtoken')
      .subscribe({
        next: response => {
          this.refreshLoginData(this._email, this._userId, this._userName, response.token, +response.expiresIn, this._supplier, this._userAvatarUrl);
        },
        error: error => {
          this.logout();
        }
      });
  }

  logout() {
    if (this._isAuthenticated) {
      this.http.get<{ status: string, error?: string; }>(environment.baseUrl + '/user/logout').subscribe();
      this._token = '';
      this._email = '';
      this._userId = '';
      this._userAvatarUrl = '';
      this._isAuthenticated = false;
      this._authStatusListener.next('logged out');
      clearTimeout(this._tokenTimer);
      this.clearData();
      this.router.navigate(['/login']);
    }
  }


  // We need to store the authentication data in localStorage in case the user refreshes the page, or closes the page, etc
  private saveAuthData(authData: object) {
    Object.entries(authData).forEach(item => {
      const [key, value] = item;
      if (key && value) {
        localStorage.setItem(key, value);
      }
    });
  }

  private clearData() {
    localStorage.clear();
    sessionStorage.clear();
  }

  // Used in autologin to retrieve auth data to log the user in
  private getAuthData() {
    return Object.keys(localStorage).reduce((data: { [key: string]: string; }, key: string): any => {
      const value = localStorage.getItem(key);
      data[key] = value as string;
      return data;
    }, {});
  }
}

interface IRegister {
  status: string;
  error?: string;
  user: {
    _id: string;
    createdAt: Date;
    email: string;
    googleUid?: string;
    totpSecret: string;
    name: string;
  };
}
